import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  groupBy,
  map,
  mergeMap,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import AppState from '../reducers/app-state';
import { ID, ReduxAction, SearchSort, SearchSortBy, SearchSortDirection } from '@repo/utils/types';
import Group, { BulkGroupUpdate, groupPageLimit } from '@repo/utils/models/Group';
import { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { selectActiveGroupView } from './groupView';
import { NavigateFunction } from 'react-router-dom';
import { selectUser } from '../reducers/user/account';
import { apiObservable, apiFileRequest } from '@repo/utils/helpers/api';
import { AjaxResponse } from 'rxjs/ajax';
import { applySortStateToRequestBody } from './search';
import { alertUser } from './alerts';
import analytics from '@repo/utils/helpers/analytics';
import { selectImportStatus } from '../reducers/data/imports';

/* Epic actions */
const groupActions = requestActions('GROUP', ['fetchAll', 'fetch', 'create', 'update', 'delete']);
const groupOrderActions = requestActions('GROUP_ORDER', ['update']);
const clearDroppedIntoGroup = () => ({ type: 'CLEAR_DROPPED_INTO_GROUP' });
const dropIntoGroup = ({ group }) => ({ type: 'DROP_INTO_GROUP', payload: group });

/* Component actions */
export const fetchGroups = groupActions['fetchAll'];
export const fetchGroup = (groupID: ID) => groupActions['fetch']({ groupID });
export const fetchMoreGroupMembers = (groupID: ID) => ({
  type: 'FETCH_MORE_GROUP_MEMBERS',
  payload: { groupID },
});
export const refetchGroupMembersPage = (groupID: ID) => ({
  type: 'REFETCH_GROUP_MEMBERS_PAGE',
  payload: { groupID },
});
export const createGroup = groupActions['create'];
export const updateGroup = groupActions['update'];
export const deleteGroup = groupActions['delete'];
export const reorderGroups = (payload: {
  newGroupOrder: ID[];
  movedGroupID: ID;
  newIndex: number;
}) => ({ type: 'UPDATE_GROUP_ORDER', payload });

export const sortGroup = (name: SearchSortBy, direction: SearchSortDirection, groupID: ID) => ({
  type: 'SET_GROUP_SORTING',
  payload: { groupID, sorting: { name, direction } },
});

export const updateGroupsInBulk = (bulkUpdate: BulkGroupUpdate) => ({
  type: 'UPDATE_GROUPS_IN_BULK',
  payload: bulkUpdate,
});

// Dispatched when navigating away from /create view or navigating to /create view. The primary
// reason for keeping a blank list separately in the store is to allow us to use a single pattern
// for determining what list to load into a view (using the URL) while also allowing us to track
// when a newly POSTed list has been persisted. We could easily otherwise run into situations
// of having duplicate new Groups in the store, one indexed by temp ID and one indexed by persisted ID.
export const clearNewGroup = () => ({ type: 'CLEAR_NEW_GROUP' });

export const promptToDeleteGroup = (
  group: Group,
  dispatch: Dispatch,
  navigate: NavigateFunction,
) => {
  window.app.confirm(
    {
      title: group.title,
      message: 'Are you sure you want to remove this group? This action is not reversible.',
      action: 'Remove Group',
      variant: 'danger',
    },
    (confirmed) => {
      if (confirmed) {
        dispatch(deleteGroup(group));
        navigate(-1);
      }
    },
  );
};

export const downloadGroupCSV = async (group: Group, dispatch: Dispatch) => {
  analytics.track('Group CSV Downloaded', { contact_count: group.count });

  if (group.count > 5000) {
    return window.app.confirm(
      {
        title: 'Large Group Download',
        message: `This group exceeds Clay's 5,000 person limit for CSV downloads. You can either use the account export option in Settings to get all of your contact data or split this group into smaller groups to download.`,
        noCancelButton: true,
        action: 'OK',
      },
      () => {},
    );
  }

  try {
    const req = await apiFileRequest(`/groups/${group.id}/export/`, {
      apiVersion: 'v2',
      headers: { 'Content-Type': 'text/csv;charset=UTF-8' },
    });

    if (req.status && req.status >= 400) {
      console.error('Error downloading Group CSV');
      dispatch(alertUser('Group CSV download failed, please try again!', 'error'));
      return;
    }

    const blob = await req.blob();
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${group.title}.csv`;
    anchor.style.display = 'none';
    const body = document.querySelector('body');
    body.appendChild(anchor);
    anchor.click();
    body.removeChild(anchor);
  } catch (error) {
    console.error(error);
    dispatch(alertUser('Group CSV download failed, please try again!', 'error'));
  }
};

export const selectGroupCollection = (state: AppState) => state.data.groups.collection;
export const selectDroppedIntoGroup = (state: AppState) => state.data.groups.droppedIntoGroup;

// We memoize the collection of groups while a *DROP INTO A GROUP* is in effect. This allows us to nicely animate
// the drop effect -- since the groups will update in response to drop of some contacts, the updates
// would cause our animating group nav display to re-render mid-animation without this memoization
export const selectGroupsForNav = createSelector(
  selectGroupCollection,
  selectDroppedIntoGroup,
  (collection, droppedIntoGroup) => {
    const groups = collection.items
      .valueSeq()
      .toArray()
      .filter((group) => group?.isPersisted);
    const [internal, userCreated] = groups.reduce(
      ([internal, userCreated], group) => {
        if (group.is_internal) {
          return [internal.concat([group]), userCreated];
        }

        let userCreatedWithAddition = userCreated;

        if (group.updated) {
          userCreatedWithAddition = userCreated.concat([group]);
        }

        return [internal, userCreatedWithAddition];
      },
      [[], []],
    );

    const groupOrder = userCreated.map(({ id }) => id);
    return { internal, userCreated, droppedIntoGroup, groupOrder, updated: collection.updated };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: (prev, next) => prev.droppedIntoGroup && next.droppedIntoGroup,
    },
  },
);

export const selectGroup = (groupID: ID, fallbackValue?: any) => (state: AppState) => {
  return state.data.groups.collection.items.get(
    String(groupID),
    fallbackValue !== undefined ? fallbackValue : new Group({ id: groupID }),
  );
};

export const selectGroupReconnectCadence = (groupID: ID) => (state: AppState) => {
  return selectGroup(groupID)(state).reconnect_cadence;
};

export const selectGroupIsImporting =
  (groupID: ID) =>
  (state: AppState): boolean => {
    const group = selectGroup(groupID)(state);
    const importUUID = group.bulk_import?.uuid;

    if (!importUUID) {
      return undefined;
    }

    const status = selectImportStatus(importUUID)(state);
    return ['pending', 'processing'].includes(status);
  };

export const selectGroupMemberIDs =
  (groupID: ID) =>
  (state: AppState): ID[] =>
    selectGroup(groupID)(state).memberIDs.toArray() || [];

export const selectNewGroup = (state: AppState) => state.data.groups.newGroup;

export const groupsForContactSelector = (contactID: ID) => (state: AppState) => {
  return contactID
    ? state.data.groups.collection.items.filter((group) => group.hasContact(contactID)).toArray()
    : [];
};

/* Epics */
export const fetchGroupsEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_ALL_GROUPS', 'NEXUS_CONVERSATION_MSG_GROUPS_RECEIVED'),
    mergeMap(({ payload }) =>
      apiObservable(
        'GET',
        '/groups',
        {
          query: { include_contact_ids: payload?.omitContactIDs ? false : true },
        },
        { apiVersion: 'v2' },
      ).pipe(
        map((response) =>
          groupActions['fetchAllSucceeded']({
            ...response,
            // If we're doing a background sync and we are on a group view, let that view update
            // its group data. We do this b/c the GET on the group will return contact data, whereas
            // the index GET of groups only includes contact IDs.

            // HMMM
            omitGroupID:
              payload?.isBackgroundSync && selectActiveGroupView(state$.value)?.group?.id,
          }),
        ),
        startWith(groupActions['fetchAllRequested']()),
        catchError((err) => of(groupActions['fetchAllFailed'](err))),
      ),
    ),
  );

const fetchGroupMembersForReduxStateObservable = (
  groupID: ID,
  state$: StateObservable<AppState>,
  { page = 1, sorting }: { page?: number; sorting?: SearchSort },
): Observable<AjaxResponse<any>> => {
  const group = selectGroup(groupID)(state$.value);
  const body = applySortStateToRequestBody(
    {
      limit: groupPageLimit,
      page,
    },
    sorting?.name || group.sorting?.name,
    sorting?.direction || group.sorting?.direction,
  );

  return apiObservable('GET', `groups/${groupID}/contacts`, { query: body }, { apiVersion: 'v2' });
};

const fetchGroupMembersEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_GROUP', 'SET_GROUP_SORTING'),
    mergeMap(({ payload }) =>
      fetchGroupMembersForReduxStateObservable(payload.groupID, state$, {
        sorting: payload.sorting,
      }).pipe(
        map((response) =>
          groupActions['fetchSucceeded']({
            ...response,
            groupID: payload.groupID,
            isBackgroundSync: payload?.isBackgroundSync,
            page: 1,
          }),
        ),
        startWith(groupActions['fetchRequested'](payload)),
        catchError((error) =>
          of(groupActions['fetchFailed']({ ...error, requestID: payload.groupID })),
        ),
      ),
    ),
  );

// The first time we fetch a group, fetch all contact members. This allows us to do "copy all" behavior without anything fancy
const fetchAllGroupMembersEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_GROUP_SUCCEEDED'),
    mergeMap(({ payload }) => {
      const id = payload.groupID;
      const group = selectGroup(id)(state$.value);
      const page = group.page + 1;

      if (!group.allContactsLoaded && group.canLoadMore) {
        return fetchGroupMembersForReduxStateObservable(id, state$, {
          page,
          sorting: group.sorting,
        }).pipe(
          map((response) =>
            groupActions['fetchSucceeded']({
              ...response,
              groupID: id,
              page,
            }),
          ),
          startWith(groupActions['fetchRequested'](payload)),
          catchError((error) => of(groupActions['fetchFailed']({ ...error, requestID: id }))),
        );
      } else {
        return of({ type: 'NOOP' });
      }
    }),
  );

const fetchMoreGroupMembersEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_MORE_GROUP_MEMBERS', 'REFETCH_GROUP_MEMBERS_PAGE'),
    mergeMap((action) => {
      const groupID = action.payload.groupID;
      const group = selectGroup(groupID)(state$.value);
      const page = action.type === 'REFETCH_GROUP_MEMBERS_PAGE' ? group.page : group.page + 1;
      return fetchGroupMembersForReduxStateObservable(groupID, state$, {
        page,
        sorting: group.sorting,
      }).pipe(
        map((response) =>
          groupActions['fetchSucceeded']({
            ...response,
            groupID,
            page,
          }),
        ),
        startWith(groupActions['fetchRequested']({ groupID })),
        catchError((error) => of(groupActions['fetchFailed']({ ...error, requestID: groupID }))),
      );
    }),
  );

export const createGroupEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('CREATE_GROUP'),
    mergeMap(({ payload }) => {
      const { memberIDs, ...group } = payload;

      return apiObservable(
        'POST',
        '/groups/',
        {
          title: group.title,
          contact_ids: memberIDs.toArray() || [],
          color: group.color,
          reconnect_cadence: group.reconnect_cadence,
        },
        { apiVersion: 'v2' },
      ).pipe(
        map((successResponse) =>
          groupActions['createSucceeded']({ ...successResponse, tempID: payload.id }),
        ),
        startWith(groupActions['createRequested'](payload)),
        catchError((err) => of(groupActions['createFailed'](err))),
      );
    }),
  );

export const updateGroupEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_GROUP'),
    groupBy(({ payload }) => payload.group.id),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        switchMap(({ payload }) => {
          const user = selectUser(state$.value);
          const groupKey = payload.group.slug == 'starred' ? payload.group.slug : payload.group.id;
          let group = state$.value.data.groups.collection.items.get(groupKey);

          return apiObservable(
            'PATCH',
            `/groups/${payload.group.slug || payload.group.id}/`,
            {
              ...payload.requestPayload,
            },
            { apiVersion: 'v2' },
          ).pipe(
            map((successResponse) =>
              groupActions['updateSucceeded']({
                ...successResponse,
                originalPayload: payload,
                user,
              }),
            ),
            startWith(groupActions['updateRequested'](payload.group)),
            catchError((err) =>
              of(groupActions['updateFailed']({ ...err, preErrorGroupState: group, groupKey })),
            ),
          );
        }),
      ),
    ),
  );

const updateGroupsInBulkEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_GROUPS_IN_BULK'),
    mergeMap(({ payload }) =>
      apiObservable('PATCH', '/groups/bulk/', payload, { apiVersion: 'v2' }).pipe(
        map((response) => ({
          type: 'UPDATE_GROUPS_IN_BULK_SUCCEEDED',
          payload: { ...response, originalPayload: payload },
        })),
        startWith({ type: 'UPDATE_GROUPS_IN_BULK_REQUESTED', payload }),
        catchError((err) => of({ type: 'UPDATE_GROUPS_IN_BULK_FAILED', payload: err })),
      ),
    ),
  );

// If an update is a drop event, we set the droppedIntoGroup (ID) in the groups slice,
// then clear this state out after 400 ms. The delay corresponds with the duration of the
// drop event animation on the corresponding group display in the navigation.
export const dropIntoGroupEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_GROUP'),
    filter(({ payload }) => !!payload.isDropEvent),
    mergeMap(({ payload }) =>
      of(payload.group).pipe(
        delay(450),
        map(() => clearDroppedIntoGroup()),
        startWith(dropIntoGroup(payload)),
      ),
    ),
  );

export const deleteGroupEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_GROUP'),
    mergeMap(({ payload }) =>
      apiObservable('DELETE', `/groups/${payload.id}/`, undefined, { apiVersion: 'v2' }).pipe(
        map(groupActions['deleteSucceeded']),
        startWith(groupActions['deleteRequested'](payload)),
        catchError((err) => of(groupActions['deleteFailed'](err))),
      ),
    ),
  );

export const reorderGroupEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_GROUP_ORDER'),
    mergeMap(({ payload }) =>
      apiObservable('PATCH', `/network/lists/${payload.movedGroupID}/order/`, {
        order: payload.newIndex + 1,
      }).pipe(
        map(groupOrderActions['updateSucceeded']),
        startWith(groupOrderActions['updateRequested'](payload)),
        catchError((err) => of(groupOrderActions['updateFailed'](err))),
      ),
    ),
  );

export const groupsEpic = combineEpics(
  fetchGroupsEpic,
  fetchGroupMembersEpic,
  fetchAllGroupMembersEpic,
  fetchMoreGroupMembersEpic,
  createGroupEpic,
  updateGroupEpic,
  updateGroupsInBulkEpic,
  dropIntoGroupEpic,
  deleteGroupEpic,
  reorderGroupEpic,
);
